import {
  NgxMatDateFormats,
  NGX_MAT_DATE_FORMATS
} from '@angular-material-components/datetime-picker';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  InspectionWaterNetworkCase,
  WaterNetworkCharacterization
} from '@authority-labs/api-interfaces';
import { WaterSupplyData } from '@authority-labs/ui/water-supply-chart/water-supply-chart.component';
import { NotesBankDialogService } from '@check-list/app/shared/dialogs/notes-bank-dialog/notes-bank-dialog.service';
import { WaterSupplyChartDialogComponent } from '@check-list/app/shared/dialogs/water-supply-chart-dialog/water-supply-chart-dialog.component';
import { isNumber } from 'lodash';
import { filter } from 'rxjs/operators';

const CUSTOM_DATE_FORMATS: NgxMatDateFormats = {
  parse: {
    dateInput: 'hh:mm, DD.MM.YYYY'
  },
  display: {
    dateInput: 'HH:mm, DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMM YYYY'
  }
};

@Component({
  selector: 'authority-labs-water-network-characterization-inspection',
  templateUrl: './water-network-characterization-inspection.component.html',
  styleUrls: ['./water-network-characterization-inspection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NGX_MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS }]
})
export class WaterNetworkCharacterizationInspectionComponent {
  @Input() waterNetworkData: WaterNetworkCharacterization;
  @Input() readonly: boolean;
  @Input() printPreview: boolean;
  @Input() systemFlow?: number;
  @Input() systemDemand?: number;
  @Output() dataChange: EventEmitter<void> = new EventEmitter();

  selectedTab = 0;

  constructor(
    private dialog: MatDialog,
    private readonly notesBankDialogService: NotesBankDialogService
  ) {}

  // tabName is now handled directly in the template via transloco

  addTest(): void {
    this.waterNetworkData.testData.push(
      new InspectionWaterNetworkCase({
        isApplicable: true,
        isPassed: true,
        systemFlow: this.systemFlow,
        systemDemand: this.systemDemand,
        testerName: this.waterNetworkData.testData[0]?.testerName
      })
    );
    this.selectedTab = this.waterNetworkData.testData.length - 1;
    this.onChange();
  }

  removeTest(): void {
    this.waterNetworkData.testData.splice(this.selectedTab, 1);
    this.onChange();
  }

  syncStats(item: InspectionWaterNetworkCase): void {
    item.systemDemand = this.systemDemand;
    item.systemFlow = this.systemFlow;
    this.setIsPassed();
  }

  showChart(item: InspectionWaterNetworkCase): void {
    const dialogConfig: MatDialogConfig = new MatDialogConfig();
    dialogConfig.minWidth = '60vw';
    dialogConfig.data = {
      staticPressure: item.staticPressure,
      residualPressure: item.residualPressure,
      flowRate: item.flowRate,
      systemFlow: item.systemFlow,
      systemDemand: item.systemDemand
    };
    this.dialog.open(WaterSupplyChartDialogComponent, dialogConfig);
  }

  showNotesBank(e: Event, inspectionWaterNetwork: InspectionWaterNetworkCase) {
    e.stopPropagation();
    this.notesBankDialogService
      .show('waterNetworkType')
      .afterClosed()
      .pipe(filter((content) => !!content))
      .subscribe((content) => {
        inspectionWaterNetwork.type = content;
        this.onChange();
      });
  }

  onChartDataChange(): void {
    this.setIsPassed();
    this.onChange();
  }

  onChange(): void {
    this.dataChange.emit();
  }

  setIsPassed(): void {
    this.waterNetworkData.testData.forEach((item) => {
      if (
        isNumber(item.staticPressure) &&
        isNumber(item.residualPressure) &&
        isNumber(item.flowRate) &&
        isNumber(item.systemFlow) &&
        isNumber(item.systemDemand)
      ) {
        const waterSupplyData = new WaterSupplyData(
          item.staticPressure,
          item.residualPressure,
          item.flowRate,
          item.systemFlow,
          item.systemDemand
        );

        item.isPassed = waterSupplyData.safetyProvided >= 0;
      }
    });
  }
}
